import { action } from 'typesafe-actions'
import {
  PreMakingTemplate,
  PreMakingTemplateDetail,
  CustomTemplateSchedule,
  PreviousDayOrderCountsForTemplateResponse,
} from './pre-making'

export interface UpdatePreMakingTemplateItem {
  timeSlotID: number
  itemID: number
  quantity: number
}

export enum preMakingTemplatesActionType {
  FETCH_PRE_MAKING_TEMPLATES = 'FETCH_PRE_MAKING_TEMPLATES',
  FETCH_PRE_MAKING_TEMPLATES_SUCCEEDED = 'FETCH_PRE_MAKING_TEMPLATES_SUCCEEDED',
  FETCH_PRE_MAKING_TEMPLATE_DETAIL = 'FETCH_PRE_MAKING_TEMPLATE_DETAIL',
  FETCH_PRE_MAKING_TEMPLATE_DETAIL_SUCCEEDED = 'FETCH_PRE_MAKING_TEMPLATE_DETAIL_SUCCEEDED',
  FETCH_PREVIOUS_DAY_ORDER_COUNTS = 'FETCH_PREVIOUS_DAY_ORDER_COUNTS',
  FETCH_PREVIOUS_DAY_ORDER_COUNTS_SUCCEEDED = 'FETCH_PREVIOUS_DAY_ORDER_COUNTS_SUCCEEDED',
  FETCH_PREVIOUS_DAY_ORDER_COUNTS_FAILED = 'FETCH_PREVIOUS_DAY_ORDER_COUNTS_FAILED',
  UPDATE_PRE_MAKING_TEMPLATE_DETAIL = 'UPDATE_PRE_MAKING_TEMPLATE_DETAIL',
  UPDATE_PRE_MAKING_TEMPLATE_DETAIL_SUCCEEDED = 'UPDATE_PRE_MAKING_TEMPLATE_DETAIL_SUCCEEDED',
  MAKE_PRE_MAKING_TEMPLATE_ACTIVE = 'MAKE_PRE_MAKING_TEMPLATE_ACTIVE',
  MAKE_PRE_MAKING_TEMPLATE_ACTIVE_SUCCEEDED = 'MAKE_PRE_MAKING_TEMPLATE_ACTIVE_SUCCEEDED',
  MAKE_PRE_MAKING_TEMPLATE_INACTIVE = 'MAKE_PRE_MAKING_TEMPLATE_INACTIVE',
  MAKE_PRE_MAKING_TEMPLATE_INACTIVE_SUCCEEDED = 'MAKE_PRE_MAKING_TEMPLATE_INACTIVE_SUCCEEDED',
  CREATE_PRE_MAKING_TEMPLATE = 'CREATE_PRE_MAKING_TEMPLATE',
  CREATE_PRE_MAKING_TEMPLATE_SUCCEEDED = 'CREATE_PRE_MAKING_TEMPLATE_SUCCEEDED',
  DELETE_CUSTOM_TEMPLATE_REQUEST = 'DELETE_CUSTOM_TEMPLATE_REQUEST',
  DELETE_CUSTOM_TEMPLATE_SUCCESS = 'DELETE_CUSTOM_TEMPLATE_SUCCESS',
  DELETE_CUSTOM_TEMPLATE_FAILURE = 'DELETE_CUSTOM_TEMPLATE_FAILURE',
  DELETE_CUSTOM_TEMPLATE_CANCELLED = 'DELETE_CUSTOM_TEMPLATE_CANCELLED',
  SET_SELECTED_STATION = 'SET_SELECTED_STATION',
  THROW_ERROR = 'THROW_ERROR',
}

export const preMakingTemplatesActions = {
  fetchPreMakingTemplates: (stationId: number) =>
    action(preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATES, {
      stationId,
    }),
  fetchPreMakingTemplatesSucceeded: (
    preMakingTemplates: PreMakingTemplate[],
    customTemplateSchedules?: CustomTemplateSchedule[]
  ) =>
    action(preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATES_SUCCEEDED, {
      preMakingTemplates,
      customTemplateSchedules,
    }),
  fetchPreMakingTemplateDetail: (templateId: number) =>
    action(preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATE_DETAIL, {
      templateId,
    }),
  fetchPreMakingTemplateDetailSucceeded: (
    templateDetail: PreMakingTemplateDetail
  ) =>
    action(
      preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATE_DETAIL_SUCCEEDED,
      {
        templateDetail,
      }
    ),
  fetchPreviousDayOrderCounts: (templateId: number) =>
    action(preMakingTemplatesActionType.FETCH_PREVIOUS_DAY_ORDER_COUNTS, {
      templateId,
    }),
  fetchPreviousDayOrderCountsSucceeded: (
    response: PreviousDayOrderCountsForTemplateResponse
  ) =>
    action(
      preMakingTemplatesActionType.FETCH_PREVIOUS_DAY_ORDER_COUNTS_SUCCEEDED,
      { response }
    ),
  fetchPreviousDayOrderCountsFailed: (error: Error) =>
    action(preMakingTemplatesActionType.FETCH_PREVIOUS_DAY_ORDER_COUNTS_FAILED, {
      error,
    }),
  updatePreMakingTemplateDetail: (
    templateId: number,
    name: string,
    description: string,
    items: UpdatePreMakingTemplateItem[],
    dates?: Date[],
    templateType?: string
  ) =>
    action(preMakingTemplatesActionType.UPDATE_PRE_MAKING_TEMPLATE_DETAIL, {
      templateId,
      name,
      description,
      items,
      dates,
      templateType,
    }),
  updatePreMakingTemplateDetailSucceeded: () =>
    action(
      preMakingTemplatesActionType.UPDATE_PRE_MAKING_TEMPLATE_DETAIL_SUCCEEDED,
      {}
    ),
  makePreMakingTemplateActive: (templateId: number) =>
    action(preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_ACTIVE, {
      templateId,
    }),
  makePreMakingTemplateActiveSucceeded: (
    templateId: number,
    isActive: boolean
  ) =>
    action(
      preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_ACTIVE_SUCCEEDED,
      {
        templateId,
        isActive,
      }
    ),
  makePreMakingTemplateInactive: (templateId: number) =>
    action(preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_INACTIVE, {
      templateId,
    }),
  makePreMakingTemplateInactiveSucceeded: (
    templateId: number,
    isActive: boolean
  ) =>
    action(
      preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_INACTIVE_SUCCEEDED,
      {
        templateId,
        isActive,
      }
    ),
  createPreMakingTemplate: (
    stationId: number,
    name: string,
    description: string,
    templateType: string
  ) =>
    action(preMakingTemplatesActionType.CREATE_PRE_MAKING_TEMPLATE, {
      stationId,
      name,
      description,
      templateType,
    }),
  createPreMakingTemplateSucceeded: (template: PreMakingTemplate) =>
    action(preMakingTemplatesActionType.CREATE_PRE_MAKING_TEMPLATE_SUCCEEDED, {
      template,
    }),
  setSelectedStation: (stationId: number | null, stationName: string | null) =>
    action(preMakingTemplatesActionType.SET_SELECTED_STATION, {
      stationId,
      stationName,
    }),
  deleteCustomTemplateRequest: (templateId: number) =>
    action(preMakingTemplatesActionType.DELETE_CUSTOM_TEMPLATE_REQUEST, {
      templateId,
    }),
  deleteCustomTemplateSuccess: (templateId: number) =>
    action(preMakingTemplatesActionType.DELETE_CUSTOM_TEMPLATE_SUCCESS, {
      templateId,
    }),
  deleteCustomTemplateFailure: (error: Error) =>
    action(preMakingTemplatesActionType.DELETE_CUSTOM_TEMPLATE_FAILURE, {
      error,
    }),
  deleteCustomTemplateCancelled: () =>
    action(preMakingTemplatesActionType.DELETE_CUSTOM_TEMPLATE_CANCELLED, {}),
  throwError: (error: Error) =>
    action(preMakingTemplatesActionType.THROW_ERROR, { error }),
}
